import React from 'react';
import { Link } from 'react-router-dom';
import QuoteWidget from '@/components/QuoteWidget';
import { Shield, CheckCircle2, TrendingUp, Lock, Gift, Wallet } from 'lucide-react';

const WholeLifePage = () => {
  const features = [
    { icon: Shield, title: "Lifetime protection", description: "Coverage that never expires as long as premiums are paid" },
    { icon: Lock, title: "Guaranteed death benefit", description: "Your beneficiaries receive the full amount, guaranteed" },
    { icon: TrendingUp, title: "Cash value accumulation", description: "Build tax-advantaged savings over time" },
    { icon: Gift, title: "Potential dividends", description: "Participating policies may earn annual dividends" }
  ];

  const uses = [
    "Long-term wealth building",
    "Estate liquidity",
    "Supplemental retirement strategies",
    "Legacy planning",
    "Business succession"
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="whole-life-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <Link to="/services" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
                ← Back to Services
              </Link>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Whole Life Insurance
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                Permanent Protection with Long-Term Value
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Whole life insurance provides lifetime coverage combined with guaranteed cash value accumulation. It's a foundation for comprehensive financial planning, offering both protection and growth potential.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {features.map((feature, idx) => {
                  const Icon = feature.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{feature.title}</p>
                        <p className="text-slate-500 text-sm">{feature.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Financial Planning Uses</h2>
              <ul className="space-y-4">
                {uses.map((item, idx) => (
                  <li key={idx} className="flex items-center space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059]" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h2 className="font-serif text-3xl text-white mb-6">Cash Value Benefits</h2>
              <div className="space-y-4 text-slate-400">
                <p>Whole life policies build cash value that grows tax-deferred. You can access this value through policy loans or withdrawals during your lifetime.</p>
                <p>The cash value provides financial flexibility—use it for emergencies, opportunities, or as a supplement to retirement income.</p>
                <p>Dividends from participating policies can be used to purchase additional coverage, reduce premiums, or accumulate interest.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Explore Whole Life Options</h2>
          <p className="text-slate-400 mb-8">Speak with an advisor to design a policy that fits your goals.</p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Request Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WholeLifePage;
